// Sign in, create an account (emailed code) and reset a password, in the black & gold
// style of the iOS LoginView / SignupView / OTPVerificationView / ForgotPasswordView.
// Same props as before: onSuccess(user) runs after the session is saved, then onClose().
// Chauffeur accounts are not signed in here; they're pointed to the app.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { PiCaretLeft, PiDeviceMobile, PiEye, PiEyeSlash, PiWarningCircle, PiX } from 'react-icons/pi';
import api from '../../utils/api';
import { saveSession } from '../../site/session';
import { apiErrorMessage } from '../../site/errors';
import OtpInput, { OTP_LENGTH } from '../../site/OtpInput';
import { Button, Eyebrow, Field, Input, RoundButton } from '../../site/ui';
import '../../site/auth.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/rooldrive/id6756207703';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same rule as the API: 8+ characters with an uppercase letter, a lowercase letter and a number.
export function passwordProblem(password) {
  if (password.length < 8) return 'Use at least 8 characters';
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password)) {
    return 'Include an uppercase letter, a lowercase letter and a number';
  }
  return null;
}

const SCREENS = {
  signin: { eyebrow: 'Welcome back', title: 'Sign in', subtitle: 'See your rides, follow your chauffeur and book faster.' },
  signup: { eyebrow: 'New here', title: 'Join RoolDrive', subtitle: 'We’ll email you a code to verify your account.' },
  'signup-code': { eyebrow: 'Verify your email', title: 'Check your inbox' },
  forgot: { eyebrow: 'Password', title: 'Reset your password', subtitle: 'Enter your email and we’ll send you a code.' },
  'forgot-code': { eyebrow: 'Password', title: 'Choose a new password' },
  driver: { eyebrow: 'Chauffeurs', title: 'Please use the RoolDrive app', subtitle: 'Chauffeur accounts work in the RoolDrive iPhone app, where you can see and accept your rides.' },
};

function PasswordInput({ id, value, onChange, autoComplete, disabled, invalid, describedBy }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="au-password">
      <Input
        id={id}
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <button
        type="button"
        className="au-password-toggle"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <PiEyeSlash aria-hidden /> : <PiEye aria-hidden />}
      </button>
    </div>
  );
}

function ErrorLine({ children }) {
  return children ? <p className="bk-error au-error" role="alert"><PiWarningCircle aria-hidden /> {children}</p> : null;
}

function AuthModal({ isOpen, onClose, onSuccess, initialMode = 'signin', initialEmail = '' }) {
  const [screen, setScreen] = useState(initialMode === 'signup' ? 'signup' : 'signin');
  const [form, setForm] = useState({
    email: initialEmail,
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null); // gold confirmation line, e.g. "Code sent again."
  const [emailTaken, setEmailTaken] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, busy, onClose]);

  // Focus the first empty field of each screen
  useEffect(() => {
    if (!isOpen) return;
    const fields = [...(dialogRef.current?.querySelectorAll('input:not([disabled]):not([readonly]), .au-focus-first') || [])];
    (fields.find((el) => el.tagName !== 'INPUT' || !el.value) || fields[0])?.focus();
  }, [isOpen, screen]);

  if (!isOpen) return null;

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
    setEmailTaken(false);
  };
  const setCode = (code) => { setForm((prev) => ({ ...prev, code })); setError(null); };

  const go = (next, keep = {}) => {
    setScreen(next);
    setTouched(false);
    setError(null);
    setNote(keep.note || null);
    setEmailTaken(false);
    setForm((prev) => ({ ...prev, code: '', password: keep.password ? prev.password : '', newPassword: '', confirmPassword: '' }));
  };

  const email = form.email.trim().toLowerCase();
  const emailError = !EMAIL_RE.test(email) ? 'Please enter a valid email address' : null;

  const run = async (task) => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await task();
    } finally {
      setBusy(false);
    }
  };

  const finishSignIn = (token, user) => {
    if (user?.role === 'driver') {
      go('driver');
      return;
    }
    saveSession(token, user);
    onSuccess?.(user);
    onClose();
  };

  // ---------- Sign in ----------
  const signIn = (e) => {
    e.preventDefault();
    setTouched(true);
    if (emailError || !form.password) return;
    run(async () => {
      try {
        const { data } = await api.post('/auth/login', { email, password: form.password });
        finishSignIn(data.token, data.user);
      } catch (err) {
        setError(apiErrorMessage(err, 'We couldn’t sign you in. Please try again.'));
      }
    });
  };

  // ---------- Sign up ----------
  const signupPasswordError = passwordProblem(form.password);
  const phoneDigits = form.phone.replace(/\D/g, '');
  const phoneError = form.phone.trim() && phoneDigits.length < 10 ? 'Please enter a full phone number, with area code' : null;
  const signupInvalid = !form.firstName.trim() || !form.lastName.trim() || emailError || phoneError || signupPasswordError;

  const sendSignupCode = (e) => {
    e?.preventDefault();
    setTouched(true);
    if (signupInvalid) return;
    run(async () => {
      try {
        await api.post('/auth/signup/send-otp', { email });
        go('signup-code', { password: true });
      } catch (err) {
        const message = apiErrorMessage(err, 'We couldn’t send the code. Please try again.');
        setEmailTaken(/already registered/i.test(message));
        setError(/already registered/i.test(message) ? 'There’s already an account with this email.' : message);
      }
    });
  };

  const resendSignupCode = () => run(async () => {
    try {
      await api.post('/auth/resend-otp', { email });
      setNote('Code sent again.');
    } catch (err) {
      setError(apiErrorMessage(err, 'We couldn’t send the code. Please try again.'));
    }
  });

  const createAccount = (e) => {
    e.preventDefault();
    if (form.code.length !== OTP_LENGTH) return;
    run(async () => {
      try {
        const { data } = await api.post('/auth/signup/verify', {
          email,
          otp: form.code,
          password: form.password,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
        });
        finishSignIn(data.token, data.user);
      } catch (err) {
        setError(apiErrorMessage(err, 'We couldn’t create your account. Please try again.'));
      }
    });
  };

  // ---------- Forgot password ----------
  const sendResetCode = (e) => {
    e?.preventDefault();
    setTouched(true);
    if (emailError) return;
    run(async () => {
      try {
        await api.post('/auth/forgot-password/send-otp', { email });
        if (screen === 'forgot-code') setNote('Code sent again.');
        else go('forgot-code');
      } catch (err) {
        setError(apiErrorMessage(err, 'We couldn’t send the code. Please try again.'));
      }
    });
  };

  const newPasswordError = passwordProblem(form.newPassword);
  const confirmError = form.confirmPassword && form.confirmPassword !== form.newPassword ? 'Passwords don’t match' : null;

  const resetPassword = (e) => {
    e.preventDefault();
    setTouched(true);
    if (form.code.length !== OTP_LENGTH || newPasswordError || !form.confirmPassword || confirmError) return;
    run(async () => {
      try {
        await api.post('/auth/forgot-password/reset', { email, otp: form.code, newPassword: form.newPassword });
        go('signin', { note: 'Password updated. Sign in with your new password.' });
      } catch (err) {
        setError(apiErrorMessage(err, 'We couldn’t reset your password. Please try again.'));
      }
    });
  };

  const meta = SCREENS[screen];
  const subtitle = screen === 'signup-code'
    ? <>Enter the 6-digit code we sent to <strong className="au-strong">{email}</strong>.</>
    : screen === 'forgot-code'
      ? <>Enter the 6-digit code we sent to <strong className="au-strong">{email}</strong>, then your new password.</>
      : meta.subtitle;
  const back = { 'signup-code': 'signup', forgot: 'signin', 'forgot-code': 'forgot' }[screen];

  return createPortal(
    <div className="bk-dialog-backdrop au-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="au-sheet" role="dialog" aria-modal="true" aria-labelledby="au-title" aria-describedby="au-subtitle" ref={dialogRef}>
        <div className="au-top">
          {back
            ? <RoundButton label="Back" icon={PiCaretLeft} onClick={() => go(back, { password: back === 'signup' })} disabled={busy} />
            : <span className="au-wordmark" aria-hidden>Rool<span>Drive</span></span>}
          <RoundButton label="Close" icon={PiX} onClick={onClose} disabled={busy} />
        </div>

        <header className="au-head">
          <Eyebrow>{meta.eyebrow}</Eyebrow>
          <h2 id="au-title" className="au-title">{meta.title}</h2>
          {subtitle && <p id="au-subtitle" className="lx-muted au-subtitle">{subtitle}</p>}
        </header>

        {note && <p className="au-note" role="status">{note}</p>}

        {screen === 'signin' && (
          <form className="au-form" onSubmit={signIn} noValidate>
            <Field label="Email" htmlFor="au-email" error={touched && emailError}>
              <Input id="au-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set('email')} disabled={busy} />
            </Field>
            <Field label="Password" htmlFor="au-password" error={touched && !form.password ? 'Please enter your password' : null}>
              <PasswordInput id="au-password" autoComplete="current-password" value={form.password} onChange={set('password')} disabled={busy} />
            </Field>
            <button type="button" className="bk-link au-forgot" onClick={() => go('forgot')}>Forgot password?</button>
            <ErrorLine>{error}</ErrorLine>
            <Button type="submit" block size="lg" loading={busy}>Sign in</Button>
            <p className="au-switch">
              New to RoolDrive? <button type="button" className="bk-link" onClick={() => go('signup')}>Create an account</button>
            </p>
          </form>
        )}

        {screen === 'signup' && (
          <form className="au-form" onSubmit={sendSignupCode} noValidate>
            <div className="au-two">
              <Field label="First name" htmlFor="au-first" error={touched && !form.firstName.trim() ? 'Required' : null}>
                <Input id="au-first" autoComplete="given-name" placeholder="First" maxLength={50} value={form.firstName} onChange={set('firstName')} disabled={busy} />
              </Field>
              <Field label="Last name" htmlFor="au-last" error={touched && !form.lastName.trim() ? 'Required' : null}>
                <Input id="au-last" autoComplete="family-name" placeholder="Last" maxLength={50} value={form.lastName} onChange={set('lastName')} disabled={busy} />
              </Field>
            </div>
            <Field label="Email" htmlFor="au-email" error={touched && emailError}>
              <Input id="au-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set('email')} disabled={busy} />
            </Field>
            <Field label="Phone" htmlFor="au-phone" hint="Optional — so your chauffeur can reach you" error={touched && phoneError}>
              <Input id="au-phone" type="tel" autoComplete="tel" placeholder="Mobile number" maxLength={20} value={form.phone} onChange={set('phone')} disabled={busy} />
            </Field>
            <Field
              label="Password"
              htmlFor="au-new-password"
              hint="8+ characters, with an uppercase letter, a lowercase letter and a number"
              error={touched && signupPasswordError}
            >
              <PasswordInput id="au-new-password" autoComplete="new-password" value={form.password} onChange={set('password')} disabled={busy} invalid={touched && Boolean(signupPasswordError)} />
            </Field>
            <ErrorLine>{error}</ErrorLine>
            {emailTaken && (
              <p className="au-switch au-switch--left">
                <button type="button" className="bk-link" onClick={() => go('signin')}>Sign in instead</button>
                {' · '}
                <button type="button" className="bk-link" onClick={() => go('forgot')}>Reset password</button>
              </p>
            )}
            <Button type="submit" block size="lg" loading={busy}>Send verification code</Button>
            <p className="au-legal">
              By creating an account you agree to our <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
            </p>
            <p className="au-switch">
              Already have an account? <button type="button" className="bk-link" onClick={() => go('signin')}>Sign in</button>
            </p>
          </form>
        )}

        {screen === 'signup-code' && (
          <form className="au-form" onSubmit={createAccount} noValidate>
            <OtpInput id="au-code" value={form.code} onChange={setCode} disabled={busy} />
            <ErrorLine>{error}</ErrorLine>
            <Button type="submit" block size="lg" loading={busy} disabled={form.code.length !== OTP_LENGTH}>Verify and create account</Button>
            <p className="au-switch">
              Didn’t get it? Check your spam folder, or{' '}
              <button type="button" className="bk-link" onClick={resendSignupCode} disabled={busy}>resend the code</button>
            </p>
          </form>
        )}

        {screen === 'forgot' && (
          <form className="au-form" onSubmit={sendResetCode} noValidate>
            <Field label="Email" htmlFor="au-email" error={touched && emailError}>
              <Input id="au-email" type="email" autoComplete="email" placeholder="you@example.com" value={form.email} onChange={set('email')} disabled={busy} />
            </Field>
            <ErrorLine>{error}</ErrorLine>
            <Button type="submit" block size="lg" loading={busy}>Send code</Button>
          </form>
        )}

        {screen === 'forgot-code' && (
          <form className="au-form" onSubmit={resetPassword} noValidate>
            <OtpInput id="au-code" value={form.code} onChange={setCode} disabled={busy} />
            <Field
              label="New password"
              htmlFor="au-reset-password"
              hint="8+ characters, with an uppercase letter, a lowercase letter and a number"
              error={touched && newPasswordError}
            >
              <PasswordInput id="au-reset-password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} disabled={busy} invalid={touched && Boolean(newPasswordError)} />
            </Field>
            <Field
              label="Confirm new password"
              htmlFor="au-confirm-password"
              error={confirmError || (touched && !form.confirmPassword ? 'Please confirm your new password' : null)}
            >
              <PasswordInput id="au-confirm-password" autoComplete="new-password" value={form.confirmPassword} onChange={set('confirmPassword')} disabled={busy} invalid={Boolean(confirmError)} />
            </Field>
            <ErrorLine>{error}</ErrorLine>
            <Button type="submit" block size="lg" loading={busy} disabled={form.code.length !== OTP_LENGTH}>Update password</Button>
            <p className="au-switch">
              Didn’t get it? <button type="button" className="bk-link" onClick={sendResetCode} disabled={busy}>Resend the code</button>
            </p>
          </form>
        )}

        {screen === 'driver' && (
          <div className="au-form au-driver">
            <span className="au-driver-icon" aria-hidden><PiDeviceMobile /></span>
            <Button block size="lg" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="au-focus-first">Get the app</Button>
            <Button variant="ghost" block onClick={onClose}>Close</Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default AuthModal;
